import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";
import { Helmet } from "react-helmet-async";
// Responsive hero: vite-imagetools genereert 3 WebP-varianten (768/1280/1920) met content-hashes.
// De preload-link hieronder gebruikt dezelfde srcset, zodat de browser exact de per-viewport variant
// preloadt die de img-tag daadwerkelijk kiest — cruciaal voor snelle mobiele LCP.
import heroSrcset from "@/assets/hero-rinqel.webp?w=768;1280;1920&format=webp&as=srcset";
// Fallback `src` = de eerste URL uit de srcset (zonder descriptor).
const heroFallback = heroSrcset.split(",")[0].trim().split(" ")[0];

const Hero = () => {
  return (
    <section className="relative min-h-[600px] overflow-hidden">
      <Helmet>
        {/* Preload de hero (LCP). imageSrcSet + imageSizes matcht de img-tag hieronder,
            zodat de browser de juiste per-viewport variant preloadt (mobiel = 768w). */}
        <link
          rel="preload"
          as="image"
          imageSrcSet={heroSrcset}
          imageSizes="100vw"
          fetchPriority="high"
        />
      </Helmet>
      {/* Background image — responsive srcset via vite-imagetools; gehashte filenames matchen de preload. */}
      <img
        src={heroFallback}
        srcSet={heroSrcset}
        sizes="100vw"
        alt="Rinqel IT-diensten: betrouwbare IT voor een vast maandbedrag"
        loading="eager"
        decoding="async"
        fetchPriority="high"
        width={1920}
        height={1280}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

      <div className="container relative z-10 flex min-h-[600px] items-center py-20 md:py-32 lg:py-40">
        <div className="max-w-2xl">
          <div>
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Maandelijks opzegbaar · Geen kleine lettertjes
            </span>
          </div>

          <h1
            data-speakable
            className="mt-6 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl lg:text-7xl"
          >
            IT zonder{" "}
            <span className="text-gradient">gedoe.</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80 md:text-xl">
            Geen uurtje-factuurtje, geen verrassingen. Gewoon duidelijke IT die werkt — voor een vast laag bedrag per maand.
          </p>

          <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row">
            <Button variant="hero" size="lg" className="text-base" asChild>
              <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="hero">
                Bereken je prijs
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" className="border-white text-white hover:bg-white/10 text-base" asChild>
              <TrackedLink to="/contact" trackLabel="Bel ons direct" trackLocation="hero">
                <Phone className="mr-1 h-4 w-4" />
                Bel ons direct
              </TrackedLink>
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Onbeperkt support
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
              Vast maandbedrag
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
              Direct een mens aan de lijn
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
